/**
 * Settings.
 *
 * Every control writes straight through to preferences — there is no Apply
 * button, because there is nothing here that benefits from being staged, and a
 * change you can see immediately is a change you can judge immediately.
 */
import { Dialog, Select, Slider, Toggle } from '../ui';
import type { Translator } from '../../i18n';
import type { AppPreferences } from '../../persistence';
import { PREFERENCE_LIMITS } from '../../persistence';
import type { LocalePreference, ThemePreference } from '../../persistence';
import type { FlowDirection } from '../../graph/model/types';

export interface SettingsDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly preferences: AppPreferences;
  readonly onChange: (patch: Partial<AppPreferences>) => void;
  readonly t: Translator['t'];
}

export function SettingsDialog({ open, onClose, preferences, onChange, t }: SettingsDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} title={t('settings.title')} size="md">
      <div className="flex flex-col gap-7">
        <Section title={t('settings.appearance')}>
          <Select
            label={t('settings.theme')}
            value={preferences.theme}
            onChange={(value) => onChange({ theme: value as ThemePreference })}
            options={[
              { value: 'system', label: t('settings.themeSystem') },
              { value: 'light', label: t('settings.themeLight') },
              { value: 'dark', label: t('settings.themeDark') },
            ]}
          />

          <Select
            label={t('settings.language')}
            description={t('settings.languageNote')}
            value={preferences.locale}
            onChange={(value) => onChange({ locale: value as LocalePreference })}
            options={[
              { value: 'system', label: t('settings.languageSystem') },
              { value: 'en', label: t('settings.languageEn') },
              { value: 'pt', label: t('settings.languagePt') },
            ]}
          />

          <Toggle
            label={t('settings.reduceMotion')}
            description={t('settings.reduceMotionHint')}
            checked={preferences.reduceMotion}
            onChange={(reduceMotion) => onChange({ reduceMotion })}
          />
        </Section>

        <Section title={t('settings.editor')}>
          <Slider
            label={t('settings.fontSize')}
            value={preferences.editorFontSize}
            min={PREFERENCE_LIMITS.editorFontSize.min}
            max={PREFERENCE_LIMITS.editorFontSize.max}
            step={1}
            onChange={(editorFontSize) => onChange({ editorFontSize })}
            format={(size) => t('settings.fontSizeValue', { size })}
          />

          <Toggle
            label={t('editor.autoUpdate')}
            description={t('settings.debounceHint')}
            checked={preferences.autoUpdate}
            onChange={(autoUpdate) => onChange({ autoUpdate })}
          />

          <Slider
            label={t('settings.debounce')}
            value={preferences.debounceMs}
            min={PREFERENCE_LIMITS.debounceMs.min}
            max={PREFERENCE_LIMITS.debounceMs.max}
            step={50}
            disabled={!preferences.autoUpdate}
            onChange={(debounceMs) => onChange({ debounceMs })}
            format={(ms) => t('settings.debounceValue', { ms })}
          />
        </Section>

        <Section title={t('settings.layout')}>
          <Select
            label={t('settings.direction')}
            value={preferences.direction}
            onChange={(value) => onChange({ direction: value as FlowDirection })}
            options={[
              { value: 'top-to-bottom', label: t('diagram.directionTopToBottom') },
              { value: 'left-to-right', label: t('diagram.directionLeftToRight') },
            ]}
          />

          <Select
            label={t('diagram.expandFunctions')}
            value={preferences.functionDisplay}
            onChange={(value) =>
              onChange({ functionDisplay: value === 'expanded' ? 'expanded' : 'collapsed' })
            }
            options={[
              { value: 'expanded', label: t('diagram.expandFunctions') },
              { value: 'collapsed', label: t('diagram.collapseFunctions') },
            ]}
          />

          {/* One control drives both axes: they are two halves of a single
              "how airy is this diagram" decision, and letting them drift apart
              produces layouts that look accidental. */}
          <Slider
            label={t('settings.spacing')}
            value={preferences.nodeSpacing}
            min={PREFERENCE_LIMITS.nodeSpacing.min}
            max={PREFERENCE_LIMITS.nodeSpacing.max}
            step={4}
            onChange={(nodeSpacing) =>
              onChange({ nodeSpacing, rankSpacing: Math.round((nodeSpacing * 4) / 3) })
            }
            unit="px"
          />
          <p className="-mt-2 text-xs text-ink-muted">{t('settings.spacingHint')}</p>

          <Toggle
            label={t('settings.showGrid')}
            checked={preferences.showGrid}
            onChange={(showGrid) => onChange({ showGrid })}
          />

          <Toggle
            label={t('diagram.minimap')}
            checked={preferences.showMinimap}
            onChange={(showMinimap) => onChange({ showMinimap })}
          />
        </Section>

        <Section title={t('settings.branchLabels')}>
          <div className="grid grid-cols-2 gap-3">
            <LabelField
              label={t('settings.yesLabel')}
              value={preferences.yesLabel}
              onChange={(yesLabel) => onChange({ yesLabel })}
            />
            <LabelField
              label={t('settings.noLabel')}
              value={preferences.noLabel}
              onChange={(noLabel) => onChange({ noLabel })}
            />
          </div>
        </Section>
      </div>
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-4">
      <h3 className="text-xs font-semibold tracking-wider text-ink-muted uppercase">{title}</h3>
      {children}
    </section>
  );
}

function LabelField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1.5 text-sm">
      <span className="font-medium text-ink">{label}</span>
      <input
        type="text"
        value={value}
        maxLength={32}
        onChange={(event) => onChange(event.target.value)}
        className="rounded-md border border-border bg-surface px-2.5 py-1.5 text-sm text-ink"
      />
    </label>
  );
}
